// frontend\src\routes\ProtectedRoute.tsx

import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { UserPermissions } from '../types';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const { user, permissions } = useAuth();
  const location = useLocation();
  const pathname = location.pathname.toLowerCase();

  // Route-to-permission map. 'warehouse' = admins, or anyone with a warehouse
  // in Admin › Users (they see the inputs that warehouse uses).
  type Required = keyof UserPermissions | 'admin' | 'warehouse' | null;
  const routePermissions: Record<string, Required> = {
    '/': 'home_page',
    '/orders': 'orders_page',
    '/admin': 'admin',
    '/analytics': 'admin',
    '/inputs': 'warehouse',
    '/barcode-production': 'barcode_production',
    '/barcode-shipping': 'barcode_shipping',
  };

  if (!user) {
    console.log('User not authenticated, redirecting to /login');
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Check for dynamic warehouse routes
  const isWarehouseOrdersRoute = pathname.match(/^\/warehouse-\d+-orders$/);
  const requiredPermission = isWarehouseOrdersRoute
    ? 'barcode_shipping'
    : Object.keys(routePermissions).reduce((perm, route) => {
        if (pathname === route || pathname.endsWith(route.replace('/', '-'))) {
          return routePermissions[route];
        }
        return perm;
      }, null as Required);

  if (requiredPermission) {
    if (requiredPermission === 'warehouse') {
      if (permissions?.user_type !== 'Admin' && !(permissions?.editable_warehouses || []).length) {
        console.log('User has no warehouse, redirecting to /');
        return <Navigate to="/" replace />;
      }
    } else if (requiredPermission === 'admin') {
      if (permissions?.user_type !== 'Admin') {
        console.log('User does not have admin access, redirecting to /');
        return <Navigate to="/" replace />;
      }
    } else if (requiredPermission && !permissions?.[requiredPermission]) {
      console.log(`User does not have permission ${requiredPermission} for route ${pathname}, redirecting to /`);
      return <Navigate to="/" replace />;
    }
  }

  // Allow unrecognized routes (e.g., API calls or dynamic routes)
  console.log(`Allowing access to route ${pathname}`);
  return <>{children}</>;
};

export default ProtectedRoute;